import React from 'react';
import {useParams} from 'react-router-dom';
import {Box, Grid, Button, Alert, CircularProgress} from '@mui/material';
import {Sync} from '@mui/icons-material';
import {useGetReviewPage} from './useGetReviewPage';
import {useGetStaffId} from '../../hooks/useGetStaffId';
import {
  ReviewStatusOneviewEnum,
  GlobalPermissionEnum,
} from '../../schema-types/globalTypes';
import ReviewsTable from '../../components/ReviewsTable/ReviewsTable';
import {generateColumns} from './helpers';
import {themeColors} from '../../theme/Theme';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import AdviserSearch from '../../components/AdviserSearch/AdviserSearch';
import {Panel} from '../../components/Panel/Panel';
import {formatStage} from '../../components/ReviewsTable/helpers';

const ReviewsPage = () => {
  const {stage: rawStage} = useParams<{stage: keyof typeof ReviewStatusOneviewEnum}>();
  const {currentUserData, staff, setStaff, setShowStaff, showStaff} = useGetStaffId();
  const staffId = staff?.id || currentUserData?.id;
  const {data, loading, error, refetch} = useGetReviewPage(staffId);

  if (error || !data) return <Alert severity="error">Error: {error?.message}</Alert>;

  const stage: ReviewStatusOneviewEnum = rawStage
    ? (rawStage.toUpperCase() as ReviewStatusOneviewEnum)
    : ReviewStatusOneviewEnum.ERROR;

  const formattedStage = formatStage(stage);

  const isValidStage = stage && Object.values(ReviewStatusOneviewEnum).includes(stage);

  const columns = generateColumns(stage);

  const reviewsDataForStage =
    isValidStage && stage
      ? data[stage]!.map((review) => ({
          ...review,
          accountName: review.account?.name ?? 'N/A',
          dateBooked: review.dateBooked
            ? new Date(review.dateBooked).toISOString()
            : 'N/A',
        }))
      : [];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {showStaff ? (
            <AdviserSearch
              visible={showStaff}
              setVisible={setShowStaff}
              setAdviser={setStaff}
              currentUserData={currentUserData}
            />
          ) : (
            <FeatureBoundary
              features={[GlobalPermissionEnum.can_access_admin_features]}
              showErrorIfDenied={false}
            >
              <Box width="100%" display="flex" justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setShowStaff(true)}>
                  Show advisor search
                </Button>
              </Box>
            </FeatureBoundary>
          )}
        </Grid>
        <Grid item xs={12}>
          <Panel
            showButton
            buttonOnClick={() => {
              refetch({curoSync: true, staffId});
            }}
            buttonTitle={
              loading ? (
                <>
                  <CircularProgress size={20} /> Syncing...
                </>
              ) : (
                <>
                  <Sync /> Sync reviews
                </>
              )
            }
            color={themeColors.coral}
            title={formattedStage}
          >
            <ReviewsTable
              rows={reviewsDataForStage}
              loading={loading}
              columns={columns}
              stage={formattedStage}
            />
          </Panel>
        </Grid>
      </Grid>
    </>
  );
};

export default ReviewsPage;
